import React, {useEffect, useState} from 'react';
import {Box, Button, Divider} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import Dialog from '@mui/material/Dialog';
import MuiDialogTitle from '@mui/material/DialogTitle';
import MuiDialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import {gql, useLazyQuery, useMutation} from '@apollo/client';
import moment from 'moment';
import {useLocation} from 'react-router-dom';
import {useSnackbar} from 'notistack';
import ApiException from '../../utils/exception';
import {useCuroSync} from './useCuroSync';
import Loading from '../Loading/Loading';
import {usePrePrecursorGQL} from '../PrecursorForm/usePrePrecursorGQL';
import {getReviewIdFromPath} from './AdminDialogHelper';
import {useSetIsJourneyToTrue} from '../../hooks/useSetIsJourneyToTrue';
import {SaltusAny} from '../../utils/SaltusAny';
import theme from '../../theme/Theme';
import {SYNC_ACCOUNTS} from './AdminDialog';
import AutoField, {ActionType} from '../AutoForm/AutoFieldNew';
import withAutoFormNew from '../AutoForm/AutoFormNew';
import {useAutoField} from '../AutoForm/useAutoForm';
import {uuidRegex} from '../PrecursorForm/uuidRegex';
import validateUUID from '../../utils/validateUUID';
import {PullEntityType} from '../../schema-types/globalTypes';

export function ManageCURO() {
  const {syncData, getSyncData, startFullSync, fullSyncLoading} = useCuroSync();
  const {enqueueSnackbar} = useSnackbar();
  const {updateReview, updateReviewLoading} = usePrePrecursorGQL();
  const {pathname} = useLocation();
  const reviewId = getReviewIdFromPath(pathname);
  const {setIsJourneyToTrue} = useSetIsJourneyToTrue();

  const [onPullAllStaff, {loading: pullAllStaffLoading}] = useMutation(gql`
    mutation pullAllStaff {
      pullAllStaff {
        ok
      }
    }
  `);
  const [
    onFetchMonthlyAccounts,
    {loading: fetchMonthlyAccountsLoading, data: fetchMonthlyAccountsData, called},
  ] = useLazyQuery(SYNC_ACCOUNTS);
  useEffect(() => {
    // get full curo sync info
    getSyncData();
  }, []);

  const syncResult = fetchMonthlyAccountsData?.fetchMonthlyAccounts
    ? 'Sync successful'
    : 'Unable to sync';

  return (
    <FormControl component="fieldset">
      <SyncAccountsWrapped formFields={[]} />
      <Box mt={2} />
      <Divider />
      {reviewId ? (
        <>
          <Box mt={2} mb={2}>
            <Button
              color="primary"
              variant="contained"
              onClick={async () => {
                await setIsJourneyToTrue({
                  variables: {
                    reviewId: validateUUID(reviewId!)!,
                  },
                });
                updateReview({
                  variables: {
                    reviewId: validateUUID(reviewId!)!,
                    isSubmittedFromOneview: false,
                  },
                }).then(({data}) => {
                  if (data?.updateReview?.ok) {
                    enqueueSnackbar('The current review is open', {variant: 'success'});
                    return;
                  }
                  enqueueSnackbar(
                    'An error occurred when re-opening the current review',
                    {variant: 'error'}
                  );
                });
              }}
              disabled={updateReviewLoading}
            >
              Re-open current review
            </Button>
          </Box>
          <Divider />
        </>
      ) : null}
      {syncData ? (
        <Box mt={2} mb={2}>
          <Typography variant="h6">Full CURO sync:</Typography>
          <Typography>
            Last sync started:{' '}
            {syncData.lastSyncStart
              ? moment.utc(syncData.lastSyncStart).format('DD/MM/YYYY - HH:MM')
              : 'None'}
          </Typography>
          <Typography>
            Last sync ended:{' '}
            {syncData.lastSyncEnd
              ? moment.utc(syncData.lastSyncEnd).format('DD/MM/YYYY - HH:MM')
              : 'None'}
          </Typography>
          <Box mt={2} />
          <Button
            color="primary"
            variant="contained"
            fullWidth
            onClick={() => startFullSync()}
            disabled={fullSyncLoading}
          >
            Start full CURO sync
          </Button>
        </Box>
      ) : (
        <Box mt={2} mb={2}>
          <Loading entityName="CURO sync" />
        </Box>
      )}
      <Box mt={2} mb={2}>
        <Button
          color="primary"
          variant="contained"
          fullWidth
          onClick={() => onPullAllStaff()}
          disabled={pullAllStaffLoading}
        >
          {pullAllStaffLoading ? 'Loading all staff...' : 'Sync all staff'}
        </Button>
      </Box>
      {called && !fetchMonthlyAccountsLoading ? syncResult : null}
    </FormControl>
  );
}

// @ts-ignore
const DialogTitle = (props) => {
  // @ts-ignore
  const {children, onClose, ...other} = props;
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <MuiDialogTitle {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="large"
          sx={{
            position: 'absolute',
            right: theme.spacing(1),
            top: theme.spacing(1),
            color: theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
};

const DialogContent = MuiDialogContent;

const throwException = () => {
  throw new ApiException('Test exception');
};

function ManageCuroDialog(props: SaltusAny) {
  const {open, handleClose} = props;
  const [error, setError] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);

  useEffect(() => {
    if (error) {
      throwException();
    }
  }, [error]);

  return (
    <Dialog onClose={handleClose} aria-labelledby="customized-dialog-title" open={open}>
      <DialogTitle>Admin settings</DialogTitle>
      <DialogContent dividers>
        <ManageCURO />
        <Divider />
        <Box mt={1}>
          <Button variant="outlined" onClick={() => setError(true)}>
            Throw exception
          </Button>
        </Box>
        <Box mt={2} />
      </DialogContent>
    </Dialog>
  );
}

export default ManageCuroDialog;

const ENTITY_TYPE_LABELS: Record<PullEntityType, string> = {
  [PullEntityType.ACCOUNT]: 'Account',
  [PullEntityType.CONTACT]: 'Contact',
  [PullEntityType.SYSTEM_USER]: 'Staff',
  [PullEntityType.REVIEW]: 'Review',
  [PullEntityType.LEAD]: 'Lead',
};

const ENTITY_TYPES = Object.values(PullEntityType).map((value) => ({
  value,
  label: ENTITY_TYPE_LABELS[value],
}));

const SyncAccountsForm: React.FC = () => {
  const {closeSnackbar, enqueueSnackbar} = useSnackbar();
  const [{value: curoIds}] = useAutoField<string>('curoIds');
  const [{value: entityType}] = useAutoField<string>('entityType');

  const [onFetchEntities, {loading: onFetchEntitiesLoading}] = useMutation(gql`
    mutation PullEntitiesFromCuro($entityType: PullEntityType!, $curoIds: [String!]!) {
      pullEntitiesFromCuro(entityType: $entityType, curoIds: $curoIds) {
        ok
        errors
      }
    }
  `);

  const fetchEntities = () => {
    closeSnackbar();
    if (!curoIds) {
      enqueueSnackbar('Please provide at least one CURO id', {variant: 'error'});
      return;
    }
    const ids = curoIds.split(',').map((id) => id.trim());
    for (const id of ids) {
      if (!id || uuidRegex.test(id) === false) {
        enqueueSnackbar(`Invalid CURO id: ${id}`, {variant: 'error'});
        return;
      }
    }
    onFetchEntities({
      variables: {
        entityType: entityType || 'ACCOUNT',
        curoIds: ids,
      },
    })
      .then((response) => {
        if (!response.data?.pullEntitiesFromCuro?.ok) {
          const errors = response.data?.pullEntitiesFromCuro?.errors || ['Unknown error'];
          enqueueSnackbar(`Failed to sync ${selectedLabel}: ${errors.join(', ')}`, {
            variant: 'error',
          });
          return;
        }
        // Handle success, e.g., show a success message
        enqueueSnackbar(`${selectedLabel}(s) synced successfully`, {
          variant: 'success',
        });
      })
      .catch((error) => {
        // Handle error, e.g., show an error message
        enqueueSnackbar(`Error syncing ${selectedLabel}: ${error.message}`, {
          variant: 'error',
        });
      });
  };

  const selectedLabel =
    ENTITY_TYPES.find((t) => t.value === entityType)?.label || 'Entities';

  return (
    <Box display="flex" flexDirection="column" gap={2} mt={2} mb={2}>
      <Typography variant="h5">Sync Entities by CURO ID</Typography>
      <Typography variant="h6">
        Select entity type and provide CURO IDs separated by commas. eg 1233,1234,1222
      </Typography>
      <AutoField
        field={{
          id: 'entityType',
          type: ActionType.select,
          label: 'Entity Type',
          options: ENTITY_TYPES,
          initialValue: 'ACCOUNT',
        }}
      />
      <AutoField
        field={{
          id: 'curoIds',
          type: ActionType.text,
          label: 'CURO IDs',
          multiline: true,
          minRows: 3,
        }}
      />
      <Button
        variant="contained"
        color="primary"
        fullWidth
        disabled={onFetchEntitiesLoading}
        onClick={() => fetchEntities()}
      >
        Sync {selectedLabel}
      </Button>
    </Box>
  );
};

const SyncAccountsWrapped = withAutoFormNew(SyncAccountsForm, {});
